import { AxiosInstance, AxiosResponse } from "axios";

/**
 * Événement global émis quand la session ne peut plus être rafraîchie.
 * AuthContext l'écoute pour se logout proprement.
 */
export const AUTH_SESSION_EXPIRED_EVENT = "auth:session-expired";

class RefreshManager {
    /**
     * Promise du refresh en cours (single-flight).
     */
    private refreshPromise: Promise<AxiosResponse> | null = null;

    /**
     * Compteur d'échecs consécutifs.
     * Sert à éviter les boucles infinies.
     */
    private failureCount = 0;

    private readonly MAX_FAILURES = 3;

    /**
     * SINGLE FLIGHT
     * Une seule requête refresh peut être exécutée simultanément.
     */
    public refresh(client: AxiosInstance): Promise<AxiosResponse> {
        if (this.refreshPromise) {
            return this.refreshPromise;
        }

        this.refreshPromise = this.execute(client)
            .then((response) => {
                // Succès → reset compteur
                this.failureCount = 0;
                return response;
            })
            .catch((error) => {
                this.failureCount += 1;

                console.warn(
                    `[RefreshManager] Refresh failed (${this.failureCount}/${this.MAX_FAILURES})`,
                    error?.response?.status,
                );

                // Trop d'échecs → on considère la session morte
                if (this.failureCount >= this.MAX_FAILURES) {
                    this.emitSessionExpired();
                }

                throw error;
            })
            .finally(() => {
                this.refreshPromise = null;
            });

        return this.refreshPromise;
    }

    /**
     * Exécute réellement le refresh.
     * On n'utilise PAS le client principal ici pour éviter
     * de repasser par les intercepteurs de réponse.
     */
    private execute(client: AxiosInstance): Promise<AxiosResponse> {
        return client.post("/auth/refresh");
    }

    /**
     * Force une session expirée (utile pour logout forcé).
     */
    public forceExpired(): void {
        this.emitSessionExpired();
    }

    /**
     * Reset manuel.
     */
    public reset(): void {
        this.refreshPromise = null;
        this.failureCount = 0;
    }

    /**
     * Émet l'événement global.
     */
    private emitSessionExpired(): void {
        if (typeof window !== "undefined") {
            window.dispatchEvent(
                new CustomEvent(AUTH_SESSION_EXPIRED_EVENT),
            );
        }
    }
}

export const refreshManager = new RefreshManager();